import axios from "axios";
import { readPaginationHeaders, toApiPage, toPageIndex } from "../utils/apiPagination";
import type {
  CheckTypeKey,
  CheckTypeOptions,
  PaymentPeriod,
  PricelistCheckTypes,
} from "./pricelist";

export type TariffPeriod = "day" | "month" | "year";
export type TariffType = "tariff" | "checks";
export type TariffStatus = "requested" | "active" | "archived";

export type TariffPaymentType = "bank" | "manual" | "auto_approved";

export interface TariffBuyResponse {
  paymentType: TariffPaymentType;
  payId?: string;
  paymentUrl?: string;
}

export interface CheckPack {
  checkType?: CheckTypeKey;
  count?: number;
}

export interface CheckPackSnapshot extends CheckPack {
  enrichItemId?: string;
  unitAmount?: number;
  unitPrice?: number;
  linePrice?: number;
}

export interface PeriodUsage {
  period?: TariffPeriod;
  year?: number;
  month?: number | null;
  day?: number | null;
  counter?: number;
  periodLimit?: number;
}

export interface PriceListUsage {
  pricelistId?: string;
  tariffName?: string;
  tariffPrice?: number;
  tariffPeriod?: PaymentPeriod | TariffPeriod | string;
  tariffAmount?: number;
  siteAmount?: number;
  emailNotification?: boolean;
  scanByScheduleEnabled?: boolean;
  policyCheckEnabled?: boolean;
  startAt?: string;
  endAt?: string;
  usage?: PeriodUsage[];
  paymentStatus?: string;
  paymentUrl?: string;
  payId?: string;
  tariffType?: TariffType;
  checkPacks?: CheckPackSnapshot[];
}

export type PolicyLimitPeriod = "day" | "week" | "month" | "year";

export interface PolicyUsageSetting {
  period?: PolicyLimitPeriod;
  limit?: number;
}

export interface PolicyUsageConsumption {
  period?: PolicyLimitPeriod;
  limit?: number;
  counter?: number;
}

export interface UserPolicySetting {
  policyCheckEnabled?: boolean;
  limitIsReached?: boolean;
  usageSettings?: PolicyUsageSetting[];
  usageConsumption?: PolicyUsageConsumption[];
}

export interface UserLimitsCounters {
  site?: number;
  policy?: number;
  it?: number;
}

export interface CheckTypeEntitlement {
  enabled: boolean;
  categoryIds?: string[];
  options?: CheckTypeOptions;
}

export type UserEntitlements = {
  siteCheck?: CheckTypeEntitlement;
  policyCheck?: CheckTypeEntitlement;
  it?: CheckTypeEntitlement;
};

export interface UserLimits {
  entryId?: string;
  userId?: number;
  periodStart?: string;
  periodEnd?: string;
  active?: boolean;
  limits?: UserLimitsCounters;
  usage?: UserLimitsCounters;
  remaining?: UserLimitsCounters;
  entitlements?: UserEntitlements | PricelistCheckTypes;
  updatedAt?: string;
}

export interface UserTariffPurchase {
  payId?: string;
  tariffType?: TariffType;
  purchasedAt?: string;
  pricelistId?: string;
  title?: string;
  period?: PaymentPeriod | string;
  amount?: number;
  price?: number;
  status?: TariffStatus | string;
  limitsDelta?: UserLimitsCounters;
  checkPacks?: CheckPackSnapshot[];
  userId?: number;
  tariffName?: string;
  createdAt?: string;
  entitlementsSnapshot?: PricelistCheckTypes | null;
  startAt?: string;
  endAt?: string;
}

export interface UserInfo {
  id?: number;
  email?: string;
  alias?: string;
  freeTariffUsed?: boolean;
  userLimits?: UserLimits | null;
  purchases?: UserTariffPurchase[];
  requestedTariffs?: UserTariffRequest[];
  activeTariffs?: PriceListUsage[];
  archivedTariffs?: PriceListUsage[] | null;
  policySetting?: UserPolicySetting;
  purchaseHistory?: UserTariffPurchase[];
}

export interface BuyTariffPayload {
  pricelistId: string;
  period: PaymentPeriod;
  amount?: number;
}

export const getUserPurchases = (userInfo?: UserInfo | null): UserTariffPurchase[] | undefined => {
  if (!userInfo) return undefined;
  if (Array.isArray(userInfo.purchases)) return userInfo.purchases;
  if (Array.isArray(userInfo.purchaseHistory)) return userInfo.purchaseHistory;
  return undefined;
};

export const getPurchaseTitle = (purchase: UserTariffPurchase): string =>
  purchase.title?.trim() || purchase.tariffName?.trim() || "";

export const getPurchaseDate = (purchase: UserTariffPurchase): string | undefined =>
  purchase.purchasedAt || purchase.createdAt;

export const fetchUserInfo = (userId: string | number, showAll = false): Promise<UserInfo> =>
  axios
    .get<UserInfo>(`/user/${userId}/info`, { params: showAll ? { showAll: true } : undefined })
    .then((resp) => resp.data);

export const fetchUserTariffHistory = (
  userId: string | number
): Promise<UserTariffPurchase[]> =>
  axios
    .get<UserTariffPurchase[]>(`/user/${encodeURIComponent(String(userId))}/purchases`)
    .then((resp) => (Array.isArray(resp.data) ? resp.data : []));

export const fetchUserTariffHistoryByUserId = (
  userId: string | number
): Promise<UserTariffPurchase[]> =>
  axios
    .get<UserTariffPurchase[]>(`/user/${encodeURIComponent(String(userId))}/purchases`)
    .then((resp) => (Array.isArray(resp.data) ? resp.data : []));

export interface UserTariffRequest {
  payId?: string;
  tariffType?: TariffType;
  status?: string;
  paymentStatus?: string;
  paymentUrl?: string;
  paymentUrlExpiration?: string;
  userId?: number;
  userName?: string;
  userEmail?: string;
  pricelistId?: string;
  tariffName?: string;
  tariffPrice?: number;
  tariffPeriod?: PaymentPeriod | TariffPeriod | string;
  tariffAmount?: number;
  checkPacks?: CheckPackSnapshot[];
  createdAt?: string;
}

export interface TariffRequestListParams {
  page?: number;
  pageSize?: number;
}

export interface TariffRequestListResponse {
  items: UserTariffRequest[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
}

export const fetchTariffRequests = (
  params: TariffRequestListParams = {}
): Promise<TariffRequestListResponse> => {
  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const apiPage = toApiPage(pageIndex);

  return axios
    .get<UserTariffRequest[]>("/user/tariff/requests", {
      params: { page: apiPage, pageSize },
    })
    .then((resp) => {
      const items = Array.isArray(resp.data) ? resp.data : [];
      const pagination = readPaginationHeaders(resp.headers ?? {}, {
        page: apiPage,
        pageSize,
        itemsLength: items.length,
      });

      return {
        items,
        page: toPageIndex(pagination.page),
        pageSize: pagination.pageSize,
        totalPages: pagination.totalPages,
        totalElements: pagination.totalElements,
      };
    });
};

export const buyTariff = (payload: BuyTariffPayload): Promise<TariffBuyResponse> =>
  axios
    .post<TariffBuyResponse>("/user/tariff/buy", {
      id: payload.pricelistId,
      period: payload.period,
      ...(payload.amount != null ? { amount: payload.amount } : {}),
    })
    .then((resp) => resp.data);

export const confirmTariffPayment = (payId: string): Promise<void> =>
  axios.post(`/user/tariff/accept/${encodeURIComponent(payId)}`).then(() => undefined);
